<template>
    <div class="weeduc-admin-page corporate-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.users.title"
                    :subtitle="t.users.subtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions" v-if="canManage">
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    @click="openCreate"
                >
                    <v-icon class="me-1" size="small">mdi-plus</v-icon>
                    {{ t.users.new }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar">
                    <CollaboratorFilters
                        :filters="filters"
                        :labels="filterLabels"
                        :sector-options="sectorOptions"
                        :role-options="roleOptions"
                        :show-type="false"
                        :show-sector="false"
                        :show-role="false"
                        :show-responsible="false"
                        @update:filters="onFiltersChange"
                    />
                </div>

                <div class="weeduc-admin-page__table-section">
                    <div v-if="loading" class="corporate-placeholder">
                        <Loader :isActive="false" />
                    </div>
                    <div v-else-if="error" class="corporate-placeholder">{{ t.common.error }}</div>
                    <template v-else>
                        <CollaboratorTable
                            :items="pagedUsers"
                            :labels="tableLabels"
                            :empty-text="t.users.empty"
                            :active-label="t.users.active"
                            :inactive-label="t.users.inactive"
                            :show-details="false"
                            @edit="openEdit"
                            @change-org="openMove"
                            @view-performance="goDetail"
                            @remove="openRemove"
                        />
                        <div
                            v-if="users.length"
                            class="weeduc-admin-page__pagination corporate-collaborators-pagination"
                        >
                            <v-select
                                v-model="pageSize"
                                :items="pageSizeOptions"
                                :label="t.collaborators.perPage"
                                variant="outlined"
                                density="compact"
                                hide-details
                                class="weeduc-admin-v-field corporate-collaborators-pagination__size"
                                @update:model-value="onPageSizeChange"
                            />
                            <span class="corporate-collaborators-pagination__info">
                                {{ paginationInfo }}
                            </span>
                            <v-pagination
                                v-model="currentPage"
                                :length="totalPages"
                                :total-visible="5"
                                density="comfortable"
                            />
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <MoveUserDialog
            v-model="moveOpen"
            :user="selectedUser"
            :labels="t.collaborators"
            :common="t.common"
            :board-options="[]"
            :sector-options="sectorOptions.filter((o) => o.value)"
            :role-options="roleOptions.filter((o) => o.value)"
            :sectors-raw="sectorsRaw"
            :roles-raw="rolesRaw"
            :saving="saving"
            @submit="saveOrganization"
        />

        <v-dialog v-model="formOpen" max-width="640" scrollable>
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">{{ formTitle }}</h2>
                        <p class="corporate-dialog__subtitle">{{ t.users.responsibleHint }}</p>
                    </div>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <section class="corporate-dialog__section">
                        <p class="corporate-dialog__section-label">{{ t.common.sectionData }}</p>
                        <div class="corporate-dialog__section-body">
                            <div class="corporate-dialog__row">
                                <v-text-field
                                    v-model="form.name"
                                    :label="t.users.name"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                                <v-text-field
                                    v-model="form.email"
                                    :label="t.users.email"
                                    type="email"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <div class="corporate-dialog__row">
                                <v-select
                                    v-model="form.sectorId"
                                    :items="sectorOptions.filter((o) => o.value)"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.users.sector"
                                    variant="outlined"
                                    density="compact"
                                    clearable
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                                <v-select
                                    v-model="form.roleId"
                                    :items="filteredRoleOptions"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.users.role"
                                    variant="outlined"
                                    density="compact"
                                    clearable
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <div class="corporate-dialog__row">
                                <v-select
                                    v-model="form.managerUserId"
                                    :items="managerOptions"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.users.manager"
                                    variant="outlined"
                                    density="compact"
                                    clearable
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                                <v-select
                                    v-model="form.status"
                                    :items="statusOptions.filter((o) => o.value)"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.users.status"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <div class="corporate-dialog__row">
                                <v-text-field
                                    v-model="form.internalCode"
                                    :label="t.users.internalCode"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                                <v-text-field
                                    v-model="form.admissionDate"
                                    :label="t.users.admissionDate"
                                    type="date"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <div class="corporate-users-responsible-box">
                                <v-switch
                                    v-model="form.isResponsible"
                                    color="primary"
                                    hide-details
                                    inset
                                    :label="t.users.responsibleSwitch"
                                />
                                <p class="corporate-dialog__hint">{{ t.users.responsibleHelp }}</p>
                            </div>
                        </div>
                    </section>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn variant="text" class="text-none" @click="formOpen = false">
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="saving"
                        @click="saveUser"
                    >
                        {{ t.common.save }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="removeOpen" max-width="480" persistent>
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <h2 class="corporate-dialog__title">{{ t.users.delete }}</h2>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <p class="corporate-dialog__hint mb-0">{{ t.users.deleteConfirm }}</p>
                    <p v-if="removeTarget?.name" class="corporate-dialog__user-line mt-3 mb-0">
                        {{ removeTarget.name }}
                    </p>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn
                        variant="text"
                        class="text-none"
                        :disabled="removing"
                        @click="closeRemove"
                    >
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="error"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="removing"
                        @click="executeRemove"
                    >
                        {{ t.users.delete }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import CollaboratorFilters from '@/components/Admin/Corporate/CollaboratorFilters.vue';
import CollaboratorTable from '@/components/Admin/Corporate/CollaboratorTable.vue';
import MoveUserDialog from '@/components/Admin/Corporate/MoveUserDialog.vue';
import usersApi, { CORPORATE_USER_TYPE } from '@/utils/corporate/users';
import organizationalUnitsApi from '@/utils/corporate/organizationalUnits';
import rolesApi from '@/utils/corporate/roles';
import { canManageCorporateStructure } from '@/utils/corporate/permissions';
import { corporateI18n } from '@/i18n/mixins';
import { mapState } from 'vuex';

export default {
    name: 'CorporateUsers',
    mixins: [corporateI18n],
    components: {
        TitlePage,
        Loader,
        CollaboratorFilters,
        CollaboratorTable,
        MoveUserDialog,
    },
    data() {
        return {
            loading: true,
            error: false,
            saving: false,
            users: [],
            rolesRaw: [],
            sectorsRaw: [],
            sectorOptions: [],
            roleOptions: [],
            managerOptions: [],
            filters: {
                search: '',
                sectorId: null,
                roleId: null,
                status: null,
                isResponsible: null,
            },
            currentPage: 1,
            pageSize: 20,
            pageSizeOptions: [10, 20, 50],
            moveOpen: false,
            selectedUser: null,
            removeOpen: false,
            removeTarget: null,
            removing: false,
            formOpen: false,
            form: this.emptyForm(),
            debounceTimer: null,
        };
    },
    computed: {
        ...mapState(['token', 'dark']),
        canManage() {
            return canManageCorporateStructure();
        },
        formTitle() {
            return this.form.id ? this.t.users.formTitleEdit : this.t.users.formTitleCreate;
        },
        filterLabels() {
            return {
                ...this.t.collaborators,
                search: this.t.users.search,
                sector: this.t.users.sector,
                role: this.t.users.role,
                status: this.t.users.status,
                responsible: this.t.users.responsible,
                yes: this.t.users.yes,
                no: this.t.users.no,
                all: this.t.collaborators.all,
            };
        },
        tableLabels() {
            return {
                ...this.t.collaborators,
                edit: this.t.users.edit,
                delete: this.t.users.delete,
                active: this.t.users.active,
                inactive: this.t.users.inactive,
            };
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.users.length / this.pageSize) || 1);
        },
        pagedUsers() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.users.slice(start, start + this.pageSize);
        },
        paginationInfo() {
            if (!this.users.length) return '';
            const from = (this.currentPage - 1) * this.pageSize + 1;
            const to = Math.min(this.currentPage * this.pageSize, this.users.length);
            const template = this.t.collaborators.showingRange || 'Mostrando {from}–{to} de {total}';
            return template
                .replace('{from}', from)
                .replace('{to}', to)
                .replace('{total}', this.users.length);
        },
        statusOptions() {
            return [
                { text: this.t.users.active, value: 'active' },
                { text: this.t.users.inactive, value: 'inactive' },
            ];
        },
        filteredRoleOptions() {
            if (!this.form.sectorId) return this.roleOptions.filter((o) => o.value);
            return this.roleOptions.filter(
                (o) => !o.value || this.rolesRaw.some((r) => r.id === o.value && r.sectorId === this.form.sectorId),
            );
        },
    },
    async created() {
        await this.loadLookups();
        await this.loadUsers();
    },
    methods: {
        emptyForm() {
            return {
                id: null,
                name: '',
                email: '',
                sectorId: null,
                roleId: null,
                managerUserId: null,
                isResponsible: false,
                status: 'active',
                type: CORPORATE_USER_TYPE.GESTOR,
                internalCode: '',
                admissionDate: null,
            };
        },
        async loadLookups() {
            const [sectors, roles] = await Promise.all([
                organizationalUnitsApi.list(this.token, { type: 'sector' }),
                rolesApi.list(this.token),
            ]);
            this.rolesRaw = roles.content || [];
            this.sectorsRaw = sectors.content || [];
            this.sectorOptions = [
                { text: this.t.collaborators.all, value: null },
                ...(sectors.content || []).map((s) => ({ text: s.name, value: s.id })),
            ];
            this.roleOptions = [
                { text: this.t.collaborators.all, value: null },
                ...(roles.content || []).map((r) => ({ text: r.name, value: r.id })),
            ];
            this.managerOptions = [
                { text: '—', value: null },
                ...((sectors.managers) || []).map((m) => ({ text: m.name, value: m.id })),
            ];
        },
        async loadUsers() {
            this.loading = true;
            this.error = false;
            try {
                const res = await usersApi.list(this.token, {
                    search: this.filters.search || undefined,
                    status: this.filters.status || undefined,
                    sectorId: this.filters.sectorId || undefined,
                    roleId: this.filters.roleId || undefined,
                    isResponsible: this.filters.isResponsible,
                    type: CORPORATE_USER_TYPE.GESTOR,
                    page: 0,
                    size: 10000,
                });
                const managers = (res.content || []).filter(
                    (user) => Number(user.type) === CORPORATE_USER_TYPE.GESTOR,
                );
                this.users = managers;
                const maxPage = Math.max(1, Math.ceil(managers.length / this.pageSize) || 1);
                if (this.currentPage > maxPage) this.currentPage = maxPage;
            } catch (e) {
                console.error(e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        onFiltersChange(next) {
            this.filters = next;
            this.currentPage = 1;
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.loadUsers(), 300);
        },
        onPageSizeChange() {
            this.currentPage = 1;
        },
        goDetail(user) {
            this.$router.push(`/dashboard/corporativo/colaboradores/${user.id}`);
        },
        openMove(user) {
            this.selectedUser = user;
            this.moveOpen = true;
        },
        async saveOrganization(payload) {
            if (!this.selectedUser) return;
            this.saving = true;
            try {
                await usersApi.updateOrganization(this.token, this.selectedUser.id, payload);
                this.moveOpen = false;
                await this.loadUsers();
            } catch (e) {
                alert(e?.response?.data?.message || e.message || this.t.common.error);
            } finally {
                this.saving = false;
            }
        },
        openRemove(user) {
            this.removeTarget = user;
            this.removeOpen = true;
        },
        closeRemove() {
            if (this.removing) return;
            this.removeOpen = false;
            this.removeTarget = null;
        },
        openCreate() {
            this.form = this.emptyForm();
            this.formOpen = true;
        },
        openEdit(user) {
            this.form = {
                id: user.id,
                name: user.name,
                email: user.email,
                sectorId: user.sectorId,
                roleId: user.roleId,
                managerUserId: user.managerUserId,
                isResponsible: Boolean(user.isResponsible),
                status: user.status || 'active',
                internalCode: user.internalCode || '',
                admissionDate: user.admissionDate || null,
            };
            this.formOpen = true;
        },
        async saveUser() {
            if (!this.form.name?.trim() || !this.form.email?.trim()) return;
            this.saving = true;
            try {
                const payload = {
                    ...this.form,
                    type: CORPORATE_USER_TYPE.GESTOR,
                };
                if (this.form.id) {
                    await usersApi.update(this.token, this.form.id, payload);
                } else {
                    await usersApi.create(this.token, payload);
                }
                this.formOpen = false;
                await this.loadLookups();
                await this.loadUsers();
            } catch (e) {
                alert(e.message || this.t.common.error);
            } finally {
                this.saving = false;
            }
        },
        async executeRemove() {
            if (!this.removeTarget) return;
            this.removing = true;
            try {
                await usersApi.remove(this.token, this.removeTarget.id);
                this.removeOpen = false;
                this.removeTarget = null;
                await this.loadUsers();
            } catch (e) {
                alert(e?.response?.data?.message || e.message || this.t.common.error);
            } finally {
                this.removing = false;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-collaborators-pagination {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
}

.corporate-collaborators-pagination__size {
    max-width: 7.5rem;
    flex: 0 0 auto;
}

.corporate-collaborators-pagination__info {
    color: #6b7280;
    font-size: 0.85rem;
    flex: 1 1 auto;
}

.corporate-collaborators-pagination :deep(.v-pagination) {
    flex: 0 0 auto;
    margin-left: auto;
}

.corporate-users-page {
    background: #f4f5f8;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(37, 8, 145, 0.06);
    width: 100%;
}

.corporate-users-header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem 1rem;
    background: linear-gradient(135deg, #fafbff 0%, #ffffff 100%);
    border-bottom: 1px solid #e8eaf0;
    border-radius: 16px 16px 0 0;
}

.corporate-users-header__lead :deep(.titlelayoutGeneric) {
    border-bottom: none;
    padding-bottom: 0;
}

.corporate-users-header__lead :deep(.titlelayoutGeneric h3) {
    font-size: 1.5rem;
    margin: 0 0 0.35rem;
    color: #1a1d26;
}

.corporate-users-shell {
    padding: 1.25rem 1.5rem 1.5rem;
    height: calc(100vh - 210px);
    overflow-y: auto;
    scrollbar-width: none;
}

.corporate-users-shell::-webkit-scrollbar {
    display: none;
}

.corporate-users-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1.15rem;
}

.corporate-users-stat {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 1rem 1.15rem;
    text-align: left;
}

.corporate-users-stat__label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #5c6370;
    margin-bottom: 0.2rem;
}

.corporate-users-stat__value {
    font-size: 1.4rem;
    font-weight: 700;
    color: #160266;
}

.corporate-users-panel {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 1.15rem 1.25rem 1.35rem;
}

.corporate-users-toolbar {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1.15rem;
    padding: 1rem;
    background: #fafbfc;
    border: 1px solid #f0f2f5;
    border-radius: 12px;
}

.corporate-users-empty {
    min-height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5c6370;
}

.corporate-users-chip {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
}

.corporate-users-chip.is-yes,
.corporate-users-chip.is-on {
    background: rgba(46, 125, 50, 0.12);
    color: #2e7d32;
}

.corporate-users-chip.is-no {
    background: #eef0f3;
    color: #5c6370;
}

.corporate-users-chip.is-off {
    background: rgba(198, 40, 40, 0.1);
    color: #c62828;
}

.corporate-users-form-hint {
    opacity: 1 !important;
    color: #5c6370 !important;
}

.corporate-users-responsible-box {
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    padding: 0.85rem 1rem;
    background: #fafbff;
}

.corporate-users-responsible-box p {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    color: #5c6370;
    line-height: 1.4;
}

@media (max-width: 960px) {
    .corporate-users-toolbar,
    .corporate-users-stats {
        grid-template-columns: 1fr;
    }
}

.dashboard.dark .corporate-users-page {
    background: #1c1d24;
    box-shadow: none;
}

.dashboard.dark .corporate-users-header {
    background: linear-gradient(135deg, #252730 0%, #201e26 100%);
    border-bottom-color: #2d2d2d;
}

.dashboard.dark .corporate-users-header__lead :deep(.titlelayoutGeneric h3) {
    color: #f1f3f7;
}

.dashboard.dark .corporate-users-stat,
.dashboard.dark .corporate-users-panel {
    background: #252730;
    border-color: #353945;
}

.dashboard.dark .corporate-users-stat strong {
    color: #e8eaf0;
}

.dashboard.dark .corporate-users-empty,
.dashboard.dark .corporate-users-form-hint,
.dashboard.dark .corporate-users-responsible-box p {
    color: #9ca3b4 !important;
}

.dashboard.dark .corporate-users-chip.is-no {
    background: #2a2e3a;
    color: #9ca3b4;
}

.dashboard.dark .corporate-users-chip.is-yes,
.dashboard.dark .corporate-users-chip.is-on {
    background: rgba(46, 125, 50, 0.22);
    color: #86efac;
}

.dashboard.dark .corporate-users-chip.is-off {
    background: rgba(198, 40, 40, 0.2);
    color: #fca5a5;
}

.dashboard.dark .corporate-users-responsible-box {
    background: #201e26;
    border-color: #353945;
}
</style>
